import React, { useState, useEffect } from "react";
import { fetchArchivedTournaments } from "services/tournamentService";
import { getRegistrationsByTournament } from "services/registrationService";
import * as Sentry from "@sentry/react";
import { Link } from "react-router-dom";

const TournamentArchive = ({ locationId, isAdmin = false }) => {
  const [archivedTournaments, setArchivedTournaments] = useState([]);
  const [expandedTournamentId, setExpandedTournamentId] = useState(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const loadArchivedTournaments = async () => {
      setIsLoading(true);
      try {
        const tournaments = await fetchArchivedTournaments(locationId);
        const sortedTournaments = tournaments.sort(
          (a, b) => new Date(b.date) - new Date(a.date)
        );

        const tournamentsWithRegistrations = await Promise.all(
          sortedTournaments.map(async (tournament) => {
            if (tournament.type === "Simple") {
              const registrations = await getRegistrationsByTournament(
                tournament.id
              );
              return { ...tournament, registrations };
            }
            return tournament;
          })
        );

        setArchivedTournaments(tournamentsWithRegistrations);
      } catch (error) {
        Sentry.captureException(error);
        console.error("Error fetching archived tournaments:", error);
      } finally {
        setIsLoading(false);
      }
    };

    loadArchivedTournaments();
  }, [locationId]);

  const toggleExpand = (tournamentId) => {
    setExpandedTournamentId(
      expandedTournamentId === tournamentId ? null : tournamentId
    );
  };

  return (
    <div className="w-full text-slate-50 flex flex-col gap-3">
      {isLoading ? (
        <p className="text-slate-400 text-center py-4">Chargement...</p>
      ) : archivedTournaments.length > 0 ? (
        <>
          {archivedTournaments.map((tournament) => (
            <div
              key={tournament.id}
              className="bg-slate-900 border border-slate-700 rounded-lg shadow-lg p-4 w-full flex flex-col"
            >
              <h2 className="text-lg md:text-xl font-bold text-amber-400 pb-2">
                {tournament.type && tournament.date ? (
                  <>
                    {tournament.type} —{" "}
                    {new Date(tournament.date).toLocaleDateString("fr-FR", {
                      day: "numeric",
                      month: "long",
                      year: "numeric",
                    })}
                  </>
                ) : (
                  tournament.name
                )}
              </h2>
              {tournament.podiums && tournament.podiums.length > 0 ? (
                <div className="space-y-2">
                  {tournament.podiums.map((podium, index) => (
                    <div key={index} className="p-2">
                      <p className="font-semibold">{podium.name || ""}</p>
                      <ul className="pl-2 text-sm md:text-md">
                        {podium.first && <li>🥇 {podium.first}</li>}
                        {podium.second && <li>🥈 {podium.second}</li>}
                        {podium.third && <li>🥉 {podium.third}</li>}
                      </ul>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-slate-300">Aucun podium enregistré.</p>
              )}
              {tournament.type === "Simple" &&
              tournament.registrations &&
              tournament.registrations.length > 0 ? (
                <button
                  onClick={() => toggleExpand(tournament.id)}
                  className="mt-4 bg-blue-600 hover:bg-blue-500 text-white px-4 py-2 rounded-md"
                >
                  {expandedTournamentId === tournament.id
                    ? "Masquer les joueurs"
                    : "Voir les joueurs"}
                </button>
              ) : (
                tournament.teams &&
                Object.keys(tournament.teams).length > 0 && (
                  <button
                    onClick={() => toggleExpand(tournament.id)}
                    className="mt-4 bg-blue-600 hover:bg-blue-500 text-white px-4 py-2 rounded-md"
                  >
                    {expandedTournamentId === tournament.id
                      ? "Masquer les équipes"
                      : "Voir les équipes"}
                  </button>
                )
              )}
              {expandedTournamentId === tournament.id && (
                <div className="mt-4">
                  {tournament.type === "Simple" ? (
                    <div>
                      <h5 className="font-semibold text-start text-amber-400">
                        Joueurs
                      </h5>
                      <ol className="list-decimal pl-5 space-y-1">
                        {tournament.registrations.map((registration, idx) => (
                          <li key={idx} className="text-slate-200">
                            {registration.name}
                          </li>
                        ))}
                      </ol>
                    </div>
                  ) : (
                    (() => {
                      const nonEmptyDivisions = Object.entries(
                        tournament.teams
                      ).filter(([, teams]) => teams && teams.length > 0);

                      return nonEmptyDivisions.map(([division, teams]) => (
                        <div key={division} className="mb-4">
                          {nonEmptyDivisions.length > 1 && (
                            <h5 className="font-semibold text-start text-amber-400">
                              {division.toUpperCase()}
                            </h5>
                          )}
                          <ol className="list-decimal pl-5 space-y-1">
                            {teams.map((team, idx) => (
                              <li key={idx} className="text-slate-200">
                                {team.players
                                  .map((player) => player.name)
                                  .join(" et ")}
                              </li>
                            ))}
                          </ol>
                        </div>
                      ));
                    })()
                  )}
                </div>
              )}
              {isAdmin && (
                <Link
                  to={`/locations/tournaments/${tournament.id}/progress`}
                  className="text-blue-400 hover:underline pt-2"
                >
                  Accéder au tableau des matchs
                </Link>
              )}
            </div>
          ))}
        </>
      ) : (
        <p className="text-slate-400 text-center">
          Aucun tournoi archivé trouvé.
        </p>
      )}
    </div>
  );
};

export default TournamentArchive;
